// covered-by: frontends/carrie/src/ask-v2/home/__tests__/AskV2ForYou.test.tsx
import {
  type AskPunttForYouRequestUpdate,
  type AskPunttForYouWaitingItem,
} from "@mg/schemas/src/christo/askPunttOpenItems";
import { EnterpriseProfileType } from "@mg/schemas/src/commons/user";
import { Heading, Text } from "@radix-ui/themes";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  AskV2RequestPanel,
  opensRequestPanel,
  useRaisedByLine,
  waitingItemTitle,
} from "./AskV2RequestPanel";
import { useAskV2ForYou } from "./useAskV2ForYou";

import {
  formatListCardRelativeDate,
  LIST_CARD_RELATIVE_TIME_CLASS,
} from "../../components/ask-puntt-list/listCardTimestamps";
import { useAppSelector } from "../../utils/hooks";
import {
  ASK_V2_CHAT_PATH,
  ASK_V2_HOME_PATH,
  ASK_V2_PROJECT_PATH,
} from "../paths";

const TITLE_ID = "ask-v2-home-for-you-title";
const WAITING_ID = "ask-v2-home-waiting-on-you-title";
const UPDATES_ID = "ask-v2-home-request-updates-title";

const ROW_CLASS =
  "flex w-full cursor-pointer flex-col items-start gap-1 rounded-xl border border-puntt-neutral-gray-7 bg-base-white p-3 text-left shadow-[0_2px_3px_rgba(34,34,1,0.06),0_3px_12px_rgba(23,26,36,0.02)] outline-none focus-visible:ring-2 focus-visible:ring-puntt-accent-8";

function iso(value: string | Date): string {
  return value instanceof Date ? value.toISOString() : value;
}

function WaitingRow({
  item,
  onOpen,
}: {
  item: AskPunttForYouWaitingItem;
  onOpen: (item: AskPunttForYouWaitingItem) => void;
}) {
  const { t } = useTranslation("ui");
  const title = waitingItemTitle(item);
  const raisedBy = useRaisedByLine(item);
  const age = formatListCardRelativeDate(iso(item.openedAt));
  // why no chat: a request from a chat outside any project names no chat to the Expert (decision 12).
  const place =
    item.projectId == null
      ? t("askV2.home.forYou.outsideProject")
      : item.projectName;
  const body = (
    <>
      <Text
        as="span"
        size="2"
        weight="medium"
        className="text-puntt-neutral-gray-12"
      >
        {title}
      </Text>
      <span className="flex flex-wrap items-center gap-2 text-[12px] text-puntt-neutral-gray-11">
        <span>{place}</span>
        {raisedBy ? <span>{raisedBy}</span> : null}
        {age ? (
          <span className={LIST_CARD_RELATIVE_TIME_CLASS}>{age}</span>
        ) : null}
      </span>
    </>
  );
  // why no link when false: the viewer owns the item but may not read its project, whose page would answer 404.
  // A request with a panel always opens it; the panel never links to the chat it came from (decision 12).
  const linked = opensRequestPanel(item) || item.projectVisible !== false;
  return (
    <li className="list-none">
      {linked ? (
        <button
          type="button"
          data-testid={`ask-v2-home-waiting-on-you-item-${item.id}`}
          aria-label={
            item.projectId == null
              ? t("askV2.home.forYou.openRequest", { title })
              : t("askV2.home.forYou.openItem", { project: place, title })
          }
          className={ROW_CLASS}
          onClick={() => onOpen(item)}
        >
          {body}
        </button>
      ) : (
        <div
          data-testid={`ask-v2-home-waiting-on-you-item-${item.id}`}
          className={`${ROW_CLASS} cursor-default`}
        >
          {body}
        </div>
      )}
    </li>
  );
}

function UpdateRow({
  update,
  onOpen,
}: {
  update: AskPunttForYouRequestUpdate;
  onOpen: (update: AskPunttForYouRequestUpdate) => void;
}) {
  const { t } = useTranslation("ui");
  const when = formatListCardRelativeDate(iso(update.settledAt));
  const place = update.chat ? update.chat.title : update.projectName;
  return (
    <li className="list-none">
      <button
        type="button"
        data-testid={`ask-v2-home-request-update-${update.id}`}
        aria-label={t("askV2.home.forYou.openUpdate", {
          project: place,
          outcome: update.resolutionNote,
        })}
        className={ROW_CLASS}
        onClick={() => onOpen(update)}
      >
        <Text
          as="span"
          size="2"
          weight="medium"
          className="text-puntt-neutral-gray-12"
        >
          {update.resolutionNote}
        </Text>
        <span className="flex flex-wrap items-center gap-2 text-[12px] text-puntt-neutral-gray-11">
          <span>{place}</span>
          {update.settledByName ? (
            <span>
              {t("askV2.home.forYou.settledBy", { name: update.settledByName })}
            </span>
          ) : null}
          {when ? (
            <span className={LIST_CARD_RELATIVE_TIME_CLASS}>{when}</span>
          ) : null}
        </span>
      </button>
    </li>
  );
}

/**
 * The v2 home "For you" panel: open items waiting on the viewer across their
 * projects, and requests they raised that an Expert settled in the last 14
 * days. A request opens its own panel (`AskV2RequestPanel`); any other waiting
 * row opens its project with the item picked up in the composer. An update
 * opens its project, or the raiser's own chat for a request from one.
 * `requestId` (the home's `?request=`, from the Expert's notice) opens that
 * request's panel.
 */
export function AskV2ForYou({ requestId }: { requestId?: string } = {}) {
  const { t } = useTranslation("ui");
  const navigate = useNavigate();
  const query = useAskV2ForYou();
  const [openRequestId, setOpenRequestId] = useState<string | null>(
    requestId ?? null,
  );
  useEffect(() => {
    if (requestId) setOpenRequestId(requestId);
  }, [requestId]);
  // why the session role: only an Expert acts on a conflict card, a non-Expert it is assigned to included.
  const viewerIsExpert =
    useAppSelector((state) => state.auth.value?.role) ===
    EnterpriseProfileType.EXPERT;
  if (!query.data) return null;
  const { waitingOnYou, requestUpdates } = query.data;
  const panelItem = openRequestId
    ? waitingOnYou.find((item) => item.id === openRequestId)
    : undefined;

  const closePanel = () => {
    setOpenRequestId(null);
    // why only from the link: the param would reopen the panel on the next render of the home.
    if (requestId) {
      void navigate({ to: ASK_V2_HOME_PATH, search: {}, replace: true });
    }
  };
  const openProject = (item: AskPunttForYouWaitingItem) => {
    if (!item.projectId) return;
    void navigate({
      to: ASK_V2_PROJECT_PATH,
      params: { projectId: item.projectId },
      search: { openItem: item.id },
    });
  };

  const openChat = (chatId: string) => {
    void navigate({ to: ASK_V2_CHAT_PATH, params: { chatId } });
  };
  const openWaiting = (item: AskPunttForYouWaitingItem) => {
    if (opensRequestPanel(item)) {
      setOpenRequestId(item.id);
      return;
    }
    openProject(item);
  };
  const openUpdate = (update: AskPunttForYouRequestUpdate) => {
    if (update.chat) {
      openChat(update.chat.id);
      return;
    }
    if (!update.projectId) return;
    void navigate({
      to: ASK_V2_PROJECT_PATH,
      params: { projectId: update.projectId },
    });
  };

  return (
    <section
      data-testid="ask-v2-home-for-you"
      aria-labelledby={TITLE_ID}
      className="flex w-full flex-col gap-4"
    >
      <Heading
        as="h2"
        id={TITLE_ID}
        size="4"
        weight="bold"
        className="text-puntt-neutral-gray-12"
      >
        {t("askV2.home.forYou.title")}
      </Heading>
      <section aria-labelledby={WAITING_ID} className="flex flex-col gap-2">
        <Heading
          as="h3"
          id={WAITING_ID}
          size="2"
          weight="medium"
          className="text-puntt-neutral-gray-11"
        >
          {t("askV2.openItems.waitingOnYou")}
        </Heading>
        {waitingOnYou.length === 0 ? (
          <Text
            as="p"
            size="2"
            color="gray"
            data-testid="ask-v2-home-waiting-on-you-empty"
          >
            {t("askV2.home.forYou.waitingEmpty")}
          </Text>
        ) : (
          <ul
            data-testid="ask-v2-home-waiting-on-you-list"
            aria-labelledby={WAITING_ID}
            className="flex flex-col gap-2"
          >
            {waitingOnYou.map((item) => (
              <WaitingRow key={item.id} item={item} onOpen={openWaiting} />
            ))}
          </ul>
        )}
        {panelItem ? (
          <AskV2RequestPanel
            // why keyed: a second notice link swaps the item under a mounted panel, and its card must not keep the
            // first request's choice and reason.
            key={panelItem.id}
            item={panelItem}
            viewerIsExpert={viewerIsExpert}
            onClose={closePanel}
            onOpenProject={openProject}
          />
        ) : null}
      </section>
      <section aria-labelledby={UPDATES_ID} className="flex flex-col gap-2">
        <Heading
          as="h3"
          id={UPDATES_ID}
          size="2"
          weight="medium"
          className="text-puntt-neutral-gray-11"
        >
          {t("askV2.home.forYou.updatesTitle")}
        </Heading>
        {requestUpdates.length === 0 ? (
          <Text
            as="p"
            size="2"
            color="gray"
            data-testid="ask-v2-home-request-updates-empty"
          >
            {t("askV2.home.forYou.updatesEmpty")}
          </Text>
        ) : (
          <ul
            data-testid="ask-v2-home-request-updates-list"
            aria-labelledby={UPDATES_ID}
            className="flex flex-col gap-2"
          >
            {requestUpdates.map((update) => (
              <UpdateRow key={update.id} update={update} onOpen={openUpdate} />
            ))}
          </ul>
        )}
      </section>
    </section>
  );
}
